import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  Eye,
  EyeOff,
  CalendarDays,
  Instagram,
  Loader2,
  MessageCircle,
  Mountain,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { homeFor, useAuth } from "@/lib/auth";
import { CatMascot, Hold, LogoFull, Tape, Topo } from "@/components/Brand";
import { LandingCommunity } from "@/components/LandingCommunity";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";


function LoginForm({ staff }) {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = staff
        ? await api.post("/auth/staff-login", { email: a, password: b })
        : await api.post("/auth/login", { phone: a, pin: b });
      signIn(data);
      nav(homeFor(data.user.role), { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const p = staff ? "staff" : "participant";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${p}-login-form`}>
      <div className="space-y-1.5">
        <Label>{staff ? "Email" : "Nomor WhatsApp"}</Label>
        <Input
          className="h-12 text-base"
          value={a}
          onChange={(e) => setA(e.target.value)}
          type={staff ? "email" : "tel"}
          placeholder={staff ? "" : "08xxxxxxxxxx"}
          required
          data-testid={`${p}-login-id-input`}
        />
      </div>
      <div className="space-y-1.5">
        <Label>{staff ? "Password" : "PIN (6 digit)"}</Label>
        <div className="relative">
          <Input
            className="h-12 pr-12 text-base tracking-widest"
            value={b}
            onChange={(e) => setB(e.target.value)}
            type={showSecret ? "text" : "password"}
            inputMode={staff ? "text" : "numeric"}
            maxLength={staff ? 100 : 6}
            required
            data-testid={`${p}-login-secret-input`}
          />
          <button
            type="button"
            onClick={() => setShowSecret((current) => !current)}
            className="absolute right-0 top-0 flex h-12 w-12 items-center justify-center text-[#3D6F4B] transition-colors hover:text-[#1A3323] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D6F4B] focus-visible:ring-offset-2"
            aria-label={showSecret ? "Sembunyikan password" : "Tampilkan password"}
            aria-pressed={showSecret}
            title={showSecret ? "Sembunyikan password" : "Tampilkan password"}
            data-testid={`${p}-login-secret-visibility-button`}
          >
            {showSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid={`${p}-login-error`}>{error}</p>}
      <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base font-bold hover:bg-[#22422E]" data-testid={`${p}-login-submit-button`}>
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {staff ? "Masuk" : "Login"}
      </Button>
      {!staff && (
        <a
          href="https://wa.me/6281318810092"
          target="_blank"
          rel="noreferrer"
          className="block text-center text-sm font-semibold text-[#3D6F4B] underline decoration-[#7C9A6E] underline-offset-4 transition-colors hover:text-[#1A3323]"
          data-testid="forgot-pin-link"
        >
          Jika lupa PIN Klik disini
        </a>
      )}
    </form>
  );
}

function UpcomingPreview({ events }) {
  if (events.length === 0) {
    return (
      <section id="event" className="border-t border-[#1A3323]/10 bg-white py-16" data-testid="landing-upcoming-events">
        <div className="mx-auto max-w-6xl px-6 lg:px-14">
          <Tape>upcoming event</Tape>
          <h2 className="mt-4 text-4xl font-extrabold text-[#1A3323]">Sesi berikutnya</h2>
          <p className="mt-4 text-[#1A3323]/70" data-testid="landing-upcoming-empty">Jadwal sesi berikutnya akan hadir di sini.</p>
        </div>
      </section>
    );
  }
  return (
    <section id="event" className="border-t border-[#1A3323]/10 bg-white py-16" data-testid="landing-upcoming-events">
      <div className="mx-auto grid max-w-6xl gap-8 px-6 lg:grid-cols-[.55fr_1.45fr] lg:px-14">
        <div><Tape>upcoming event</Tape><h2 className="mt-4 text-4xl font-extrabold text-[#1A3323]">Sesi berikutnya</h2><p className="mt-3 text-[#1A3323]/70">Datang dengan rencana yang tenang. Check-in dan tiket dibuka pada hari event.</p></div>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="landing-upcoming-event-list">
          {events.map((event, index) => (
            <div key={event.id} className="rounded-lg border border-[#1A3323]/10 bg-[#EFEBDD] p-5" data-testid={`landing-upcoming-event-card-${index + 1}`}>
              <div className="text-xs font-extrabold uppercase tracking-[.14em] text-[#3D6F4B]">Upcoming Event</div><h3 className="mt-2 text-xl font-extrabold text-[#1A3323]">{event.name}</h3>
              <div className="mt-4 space-y-2 text-sm text-[#1A3323]/75"><div className="flex gap-2"><CalendarDays className="h-4 w-4" />{event.date} · {event.start_time}–{event.end_time}</div><div className="flex gap-2"><MapPin className="h-4 w-4" />{event.location}</div></div>
              {event.event_type === "PAID" ? (
                <p className="mt-4 inline-flex rounded-full bg-[#1A3323] px-3 py-2 text-xs font-extrabold uppercase tracking-wide text-[#FDFCF8]" data-testid="landing-paid-event-highlight">Segera Sign Up. Kuota Terbatas</p>
              ) : (
                <p className="mt-4 text-sm font-semibold text-[#1A3323]">Check-in dan tiket dibuka pada hari event.</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  useEffect(() => {
    if (params.get("expired")) toast.error("Sesi berakhir. Silakan login kembali.");
  }, [params]);
  useEffect(() => { api.get("/public/upcoming-events").then(({ data }) => setUpcomingEvents(data.events || [])).catch(() => setUpcomingEvents([])); }, []);
  if (!loading && user) return <Navigate to={homeFor(user.role)} replace />;
  return (
    <div className="landing-shell">
      <div id="beranda" className="grid min-h-screen lg:grid-cols-[1.15fr_1fr]">
        <div className="login-hero grain relative overflow-hidden bg-[#EFEBDD] px-6 pb-8 pt-8 lg:px-14 lg:py-14">
        <Topo className="absolute -right-10 top-0 h-[420px] w-full text-[#3D6F4B]/20" />
        <Hold className="absolute right-[14%] top-[18%] h-8 w-10 -rotate-12" color="#7C9A6E" />
        <Hold className="absolute bottom-[38%] right-[30%] hidden h-12 w-14 rotate-45 lg:block" color="#3D6F4B" />
        <div className="relative flex h-full flex-col lg:justify-center lg:gap-10">
          <div className="flex items-start justify-between gap-4">
            <LogoFull className="h-20 self-start sm:h-24 lg:h-36 rise" />
          </div>
          <div className="mt-4 lg:mt-0">
            <Tape className="mb-4">komunitas climbing</Tape>
            <h1 className="max-w-lg text-4xl font-extrabold leading-[1.02] tracking-tight text-[#1A3323] sm:text-5xl lg:text-6xl">Yuk manjat bareng. Ketemu di wall!</h1>
            <p className="mt-4 hidden max-w-md text-base text-[#2A2E2B]/75 sm:block">Check-in, ambil tiket, dan tunggu giliranmu tanpa ribet. Keeper yang panggil, kamu tinggal naik.</p>
          </div>
          <CatMascot jump className="relative -mt-2 ml-auto w-24 sm:w-36 lg:absolute lg:bottom-6 lg:right-6 lg:mt-0 lg:w-52" />
        </div>
        </div>
        <div className="login-panel flex items-center justify-center px-5 py-10">
        <div className="login-form-shell w-full max-w-md rise rise-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-[#1A3323]">
                Masuk dulu, yuk
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Climber masuk pakai nomor WhatsApp & PIN.
              </p>
            </div>
            <LanguageSelector className="shrink-0" />
          </div>
          <Tabs defaultValue="participant">
            <TabsList className="mb-5 mt-6 grid h-11 w-full grid-cols-2">
              <TabsTrigger value="participant" data-testid="login-tab-participant">Climber</TabsTrigger>
              <TabsTrigger value="staff" data-testid="login-tab-staff">Keeper / Admin</TabsTrigger>
            </TabsList>
            <TabsContent value="participant"><LoginForm /></TabsContent>
            <TabsContent value="staff"><LoginForm staff /></TabsContent>
          </Tabs>
          <div className="mt-8 flex items-center gap-3 rounded-2xl bg-[#1A3323] p-4 text-[#FDFCF8]">
            <Mountain className="h-6 w-6 shrink-0 text-[#B9D3A8]" />
            <div className="flex-1 text-sm">Baru di sini? Daftar sekali, waiver cukup disetujui sekali.</div>
            <Link to="/daftar" data-testid="go-register-link">
              <Button variant="secondary" className="rounded-full font-bold">Daftar</Button>
            </Link>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <a href="https://www.instagram.com/iyalagi.manjat/" target="_blank" rel="noreferrer" className="contact-link" data-testid="login-instagram-link"><Instagram className="h-4 w-4" /> Instagram</a>
            <a href="https://chat.whatsapp.com/L6uTOVj37ha90eLFaYWXFn?s=cl&p=i&mlu=0&ilr=4" target="_blank" rel="noreferrer" className="contact-link" data-testid="login-whatsapp-link"><MessageCircle className="h-4 w-4" /> Join Group</a>
          </div>
        </div>
        </div>
      </div>
      <LandingCommunity />
      <UpcomingPreview events={upcomingEvents} />
      <section id="komunitas" className="bg-[#1A3323] px-6 py-16 text-[#FDFCF8]">
        <div className="mx-auto flex max-w-6xl flex-col justify-between gap-7 sm:flex-row sm:items-end">
          <div>
            <Tape className="bg-[#E3EDD9]">Partnership</Tape>
            <h2 className="mt-4 text-4xl font-extrabold">Ingin Kerjasama atau Private Trip?</h2>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-[#FDFCF8]/75" data-testid="partnership-cta-description">
              Ajak tim, teman, atau komunitasmu untuk membangun pengalaman climbing yang aman dan seru bersama kami.
            </p>
          </div>
          <a href="https://wa.me/6281318810092" target="_blank" rel="noreferrer" className="inline-flex min-h-14 w-full items-center justify-center rounded-full bg-[#E3EDD9] px-8 text-base font-extrabold text-[#1A3323] sm:mb-1 sm:w-auto sm:min-w-52 sm:px-10" data-testid="landing-primary-cta">Hubungi Kami</a>
        </div>
      </section>
      <footer id="faq" className="bg-[#F7F4EA] px-6 py-8 text-sm text-[#1A3323]/70"><div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4"><span data-testid="landing-footer-brand">Iya Lagi Manjat · Komunitas Pemanjat Kalcer</span><div className="flex gap-4"><a href="https://www.instagram.com/iyalagi.manjat/" target="_blank" rel="noreferrer">Instagram</a><a href="https://chat.whatsapp.com/L6uTOVj37ha90eLFaYWXFn?s=cl&p=i&mlu=0&ilr=4" target="_blank" rel="noreferrer">Join Group</a></div></div></footer>
    </div>
  );
}
